import { LAYOUT, MODE, DRAWINGMODE, RENDERER_MODE } from '@/constants';
import camera from './camera';
import { defineVuexModule } from '../helpers';
import { MutationTree } from 'vuex';

type DeepValues<T extends object> = { [P in keyof T]: T[P] extends object ? DeepValues<T[P]> : T[P] }[keyof T];

export default defineVuexModule({
  namespaced: true,
  state: {
    layout: LAYOUT.NOTSELECTED as (typeof LAYOUT)[keyof typeof LAYOUT],
    mode: (RENDERER_MODE ? MODE.RENDERER : MODE.LAYOUTCHOOSER) as (typeof MODE)[keyof typeof MODE],
    previousMode: null as string | null,
    drawingMode: DRAWINGMODE.MAIN as DeepValues<typeof DRAWINGMODE>,
    selectionEnabled: false,
    shiftKey: false,
  },

  getters: {
    currentMode: (state) => state.mode,
    previousMode: (state) => state.previousMode,
    isLabelsMode: (state) => state.mode === MODE.LABELS,
    isCategoriesMode: (state) => state.mode === MODE.CATEGORIES,
    isPosMode: (state) => state.mode === MODE.POS,
    isBackgroundMode: (state) => state.mode === MODE.DRAWING && state.drawingMode === DRAWINGMODE.BGIMAGE,
    isDrawingMode: (state) => state.mode === MODE.DRAWING,
    isBlockSeatsMode: (state) => state.mode === MODE.BLOCKSEATS,
    isZonesLayout: (state) => state.layout === LAYOUT.SECTIONS,
    isInsideZoneLayout: (state) => state.layout === LAYOUT.INSIDESECTION,
    rowsLayout: () => LAYOUT.ROWS,
    isRowsLayout: (state, getters) => state.layout === getters.rowsLayout,
    tablesLayout: () => LAYOUT.TABLES,
    isTablesLayout: (state, getters) => state.layout === getters.tablesLayout,
    mixedLayout: () => LAYOUT.MIXED,
    isMixedLayout: (state, getters) => state.layout === getters.mixedLayout,
    isLayoutSelected: (state) => state.layout !== LAYOUT.NOTSELECTED,
    isDrawingRowsEnabled: (state, getters) =>
      getters.isLayoutSelected && (getters.isRowsLayout || getters.isMixedLayout || getters.isInsideZoneLayout),
    isDrawingTablesEnabled: (state, getters) =>
      getters.isLayoutSelected && (getters.isTablesLayout || getters.isMixedLayout || getters.isInsideZoneLayout),
    isDrawingSectionsEnabled: (state, getters) => getters.isLayoutSelected && getters.isZonesLayout,
    modes: () => [
      MODE.LAYOUTCHOOSER,
      MODE.INITIAL,
      MODE.DRAWING,
      MODE.CATEGORIES,
      MODE.LABELS,
      MODE.POS,
      MODE.BLOCKSEATS,
      // MODE.BGIMAGE,
    ],
  },

  actions: {
    setLayoutChooserMode(context) {
      context.dispatch('changeLayout', LAYOUT.NOTSELECTED);
      context.dispatch('changeMode', MODE.LAYOUTCHOOSER);
    },
    setInitialMode(context) {
      context.dispatch('changeMode', MODE.INITIAL);
    },
    setDrawingMode(context) {
      context.dispatch('changeMode', MODE.DRAWING);
    },
    setLabelsMode(context) {
      context.dispatch('changeMode', MODE.LABELS);
    },
    setSeatsMode(context) {
      context.dispatch('changeMode', MODE.CATEGORIES);
    },
    setRendererMode(context) {
      context.dispatch('changeMode', MODE.RENDERER);
    },
    setPosMode(context) {
      context.dispatch('changeMode', MODE.POS);
    },
    setBlockSeatsMode(context) {
      context.dispatch('changeMode', MODE.BLOCKSEATS);
    },
    setZonesLayout(context) {
      context.dispatch('changeLayout', LAYOUT.SECTIONS);
    },
    setRowsLayout(context) {
      context.dispatch('changeLayout', LAYOUT.ROWS);
    },
    setTablesLayout(context) {
      context.dispatch('changeLayout', LAYOUT.TABLES);
    },
    setNotSelectedLayout(context) {
      context.dispatch('changeLayout', LAYOUT.NOTSELECTED);
    },
    setMixedLayout(context) {
      context.dispatch('changeLayout', LAYOUT.MIXED);
    },
    setInsideZoneLayout(context) {
      if (context.getters.isInsideZoneLayout) return;
      context.dispatch('changeLayout', LAYOUT.INSIDESECTION);
    },
    changeLayout(context, layout) {
      context.commit('layout', layout);
    },
    changeMode({ commit, state }, mode) {
      if (state.mode === mode) return;
      commit('mode', mode);
    },
    changeDrawingMode({ commit, state }, mode) {
      if (state.drawingMode === mode) return;
      commit('drawingMode', mode);
    },
    enableSelection(context) {
      context.dispatch('setSelection', true);
    },
    disableSelection(context) {
      context.dispatch('setSelection', false);
    },
    setSelection({ commit, state }, flag) {
      if (state.selectionEnabled === flag) return;
      commit('selection', flag);
    },

    setShiftKey({ commit, state }, flag) {
      if (state.shiftKey === flag) return;
      commit('shiftKey', flag);
    },
  },

  mutations: {
    mode(state, mode) {
      state.previousMode = state.mode;
      state.mode = mode;
    },
    drawingMode(state, mode) {
      state.drawingMode = mode;
    },
    selection(state, flag) {
      state.selectionEnabled = flag;
    },
    layout(state, layout) {
      state.layout = layout;
    },
    shiftKey(state, flag) {
      state.shiftKey = flag;
    },
  },
});
